/* SHARED CONTRACT with wardrobe-crm — section and param key names must stay identical. version 1 */
/*
 * WD_CATALOGUE — the single source of truth for what a customer can pick in the
 * Wardrobe Designer: constraints, defaults and render hints. No prices anywhere in
 * here; the estimate is produced later, by a human, from the enquiry this catalogue
 * helps build. The controller renders its UI from this object rather than hand-coding
 * each step, and wardrobe-geometry.js / wardrobe-render.js both read it.
 *
 * Door counts, panel materials, frame colours and the width ceiling are taken from
 * the live product catalogue (41 sliding door products), not invented:
 *   Size option -> 2 doors to 2400mm, 3 doors 2401-3600mm, 4 doors 3601-4800mm.
 * Sliding is the only door type the business sells.
 *
 * Vanilla ES2020, no build step. Exposes window.WD_CATALOGUE only.
 */
(function () {
  'use strict';

  var WD_CATALOGUE = {
    version: 1,

    opening: {
      width: {
        min: 600,
        max: 4800,
        step: 10,
        default: 2400,
        presets: [
          { label: 'Single', value: 1200 },
          { label: 'Standard', value: 1800 },
          { label: 'Wide', value: 2400, default: true },
          { label: 'Full wall', value: 3600 }
        ]
      },
      height: {
        min: 1200,
        max: 2700,
        step: 10,
        default: 2100,
        presets: [
          { label: '2100 standard', value: 2100, default: true },
          { label: '2400 high', value: 2400 },
          { label: '2700 high', value: 2700 }
        ]
      },
      depth: {
        options: [
          { value: 440, label: '440mm standard', sub: 'Suits most bedrooms', default: true },
          { value: 580, label: '580mm deep', sub: 'Walk-in feel, room for chunky hangers' }
        ]
      }
    },

    doors: {
      /* Sliding only. Kept as a list so the geometry module keeps its shape and a
         second type could return without a refactor. */
      types: [
        {
          value: 'sliding',
          label: 'Sliding',
          default: true,
          geom: { overlap: 30, topTrack: 35, botTrack: 10, maxPanel: 1250, minPanel: 600 }
        }
      ],

      /* How many panels the opening takes. Bands mirror the live Size option; the
         customer may pick any count whose resulting panel width stays inside
         minPanel..maxPanel, so the choice is always something we can actually build. */
      countBands: [
        { maxWidth: 2400, count: 2 },
        { maxWidth: 3600, count: 3 },
        { maxWidth: 4800, count: 4 }
      ],
      countOptions: [2, 3, 4],

      /* Panel materials, one per real product range. */
      inserts: [
        { value: 'mirror', label: 'Mirror', default: true, fill: '#EAEFF3', streak: true },
        { value: 'grey-mirror', label: 'Grey Mirror', fill: '#D7DCE0', streak: true },
        { value: 'super-white-glass', label: 'Super White Glass', fill: '#F6F8F8' },
        { value: 'black-glass', label: 'Black Glass', fill: '#2B2F35' },
        { value: 'bronze-glass', label: 'Bronze Glass', fill: '#C9AE8A' },
        { value: 'white-vinyl', label: 'White Vinyl', fill: '#F4F3EF' },
        { value: 'black-vinyl', label: 'Black Vinyl', fill: '#33363B' },
        { value: 'mdf', label: 'MDF Routed', sub: 'Hampton, Colonial, VJ and more', fill: '#F1ECE3' },
        { value: 'polytec', label: 'Polytec Timber Look', sub: 'Oak and walnut finishes', fill: '#DFCBAC' }
      ],

      /* Frame colours as supplied by the client. */
      frames: [
        { value: 'matt-natural', label: 'Matt Natural', swatch: '#C7C3BB', default: true },
        { value: 'white', label: 'White', swatch: '#F4F4F2' },
        { value: 'black', label: 'Black', swatch: '#1A1B1E' },
        { value: 'chrome', label: 'Chrome', swatch: '#C3C8CE' }
      ]
    },

    sections: {
      drawer: {
        label: 'Drawer tower',
        min: 400,
        max: 800,
        def: 500,
        flex: false,
        params: {
          drawers: { options: [3, 4, 5, 6], default: 4 },
          hangAbove: {
            default: true,
            labels: { true: 'Hanging above', false: 'Shelves above' }
          }
        }
      },
      shelf: {
        label: 'Shelf tower',
        min: 400,
        max: 800,
        def: 500,
        flex: false,
        params: {
          shelves: { options: [4, 5, 6, 7], default: 5 }
        }
      },
      single: {
        label: 'Single hang',
        min: 500,
        max: 1400,
        def: 900,
        flex: true,
        midSupportOver: 1200
      },
      double: {
        label: 'Double hang',
        min: 500,
        max: 1400,
        def: 1000,
        flex: true,
        midSupportOver: 1200
      }
    },

    panelAllowance: 16,
    topShelf: { default: true, label: 'Top shelf across the full width' },

    layouts: [
      {
        id: 'organiser',
        label: 'The Organiser',
        sub: 'Drawers both ends, hanging in the middle',
        minWidth: 2000,
        default: true,
        recipe: ['drawer', '*double', 'drawer']
      },
      {
        id: 'organiser-s',
        label: 'The Organiser',
        sub: 'Drawers one end, hanging beside',
        minWidth: 1100,
        maxWidth: 1999,
        default: true,
        recipe: ['drawer', '*double']
      },
      {
        id: 'all-hang',
        label: 'All Hanging',
        sub: 'Maximum hanging space',
        minWidth: 600,
        recipe: ['*double']
      },
      {
        id: 'half-half',
        label: 'Half and Half',
        sub: 'Hanging one side, shelves the other',
        minWidth: 1000,
        recipe: ['*single', 'shelf']
      },
      {
        id: 'family',
        label: 'The Family Robe',
        sub: 'Drawers, shelves and double hanging',
        minWidth: 2700,
        recipe: ['drawer', '*double', 'shelf']
      },
      {
        id: 'simple',
        label: 'Keep it Simple',
        sub: 'A rail and a shelf, done',
        minWidth: 600,
        recipe: ['*single']
      }
    ],

    install: [
      { value: true, label: 'Supply and install', sub: 'Our fitters, rubbish taken away', default: true },
      { value: false, label: 'Supply only', sub: 'Flat packs, you fit it' }
    ]
  };

  window.WD_CATALOGUE = WD_CATALOGUE;
})();
